import React, { useEffect, useState } from "react";
import { Button, DetailDrawer, InnoTable } from "InnoUI/components";
import { apiGet } from "api/callApi";
import TimezoneConverter from "components/TimeZoneConvertor";
import theme from "theme";
import SC from "../style";
import CartHistoryDetails from "./CartHistoryDetails";

const sendTypeLabel = {
  2: "Download link - no login",
  3: "Download link - login required",
  4: "Attachments",
};

const sendTypeOptions = Object.entries(sendTypeLabel).map(([value, text]) => ({
  text,
  value: Number(value),
}));

const sendTypeColumnFilter = {
  filters: sendTypeOptions,
  onFilter: (value, record) => {
    // ensure numeric comparison
    return Number(record?.sendType) === Number(value);
  },
  filterMultiple: false,
};

const CartHistory = ({
  search = "",
  dateFrom = "",
  dateTo = "",
  resetKey = 0,
  historyTotalRecords = 0,
  historyData = [],
  historyLoading,
  historyPage,
  historyPageSize,
  setHistoryPage,
  setHistoryPageSize,
  setSendTypeFilter,
  setSortField,
  setSortOrder,
}) => {
  const [selectedHistory, setSelectedHistory] = useState(null);
  const [historyDetailsLoading, setHistoryDetailsLoading] = useState(false);

  const closeHistoryDrawer = () => {
    setSelectedHistory(null);
    setHistoryDetailsLoading(false);
  };

  const getCartHistoryDetails = async (record) => {
    if (!record?.id) return;

    try {
      setSelectedHistory(record);
      setHistoryDetailsLoading(true);
      const response = await apiGet(`/v1/cart/history/details/${record.id}`);
      setSelectedHistory(response?.data || record);
    } catch (error) {
      console.log("Error fetching cart history details", error);
    } finally {
      setHistoryDetailsLoading(false);
    }
  };

  const onFilterChange = (filters, sorter, pagination) => {
    const sendType = filters?.sendType?.[0] ?? null;
    setSendTypeFilter(sendType);

    if (sorter?.field && sorter?.order) {
      setSortField(sorter.field);
      setSortOrder(sorter.order === "ascend" ? "asc" : "desc");
    } else if (sorter?.field && !sorter?.order) {
      setSortField("createdOn");
      setSortOrder("desc");
    }

    // If pagination object provided, apply it (user clicked page or changed size)
    if (pagination && (pagination.current || pagination.pageSize)) {
      if (pagination.current) setHistoryPage(Number(pagination.current));
      if (pagination.pageSize) setHistoryPageSize(Number(pagination.pageSize));
    } else {
      // filters/sort change: reset to first page
      setHistoryPage(1);
    }
  };

  // Reset to page 1 when any parent filter changes
  useEffect(() => {
    setHistoryPage(1);
  }, [search, dateFrom, dateTo, setHistoryPage]);

  // Reset all internal filter/sort state when parent triggers reset
  useEffect(() => {
    if (resetKey === 0) return;
    setSendTypeFilter(null);
    setSortField("createdOn");
    setSortOrder("desc");
    setHistoryPage(1);
  }, [resetKey, setHistoryPage, setSendTypeFilter, setSortField, setSortOrder]);

  const historyTableConfig = {
    draggable: false,
    selection: false,
    expandable: false,
    sno: false,
    addNew: false,
    pagination: {
      current: historyPage,
      pageSize: historyPageSize,
      total: historyTotalRecords,
      showSizeChanger: true,
      pageSizeOptions: [10, 20, 50, 100],
      showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
      onChange: (page) => {
        setHistoryPage(Number(page));
      },
      onShowSizeChange: (current, size) => {
        setHistoryPage(Number(current));
        setHistoryPageSize(Number(size));
      },
    },
    search: false,
    action: false,
    divBorder: false,
  };

  const historyColumns = [
    {
      title: "S.No.",
      dataIndex: "sno",
      width: 80,
      render(value, record, index) {
        const rowIndex = Number.isInteger(index)
          ? index
          : historyData.findIndex((item) => item.id === record.id);

        return (
          <SC.ValueSpan>
            {rowIndex >= 0
              ? (historyPage - 1) * historyPageSize + rowIndex + 1
              : "--"}
          </SC.ValueSpan>
        );
      },
    },
    {
      title: "Journal-Subject",
      dataIndex: "journalSubject",
      render(value) {
        return <SC.ValueSpanP>{value || "--"}</SC.ValueSpanP>;
      },
    },
    {
      title: "Send Type",
      dataIndex: "sendType",
      render(value) {
        return <SC.ValueSpanP>{sendTypeLabel[value] || "--"}</SC.ValueSpanP>;
      },
      ...sendTypeColumnFilter,
    },
    {
      title: "Sender",
      dataIndex: "senderName",
      render(value, record) {
        return (
          <SC.ValueSpanP>{value || record.senderEmail || "--"}</SC.ValueSpanP>
        );
      },
    },
    {
      title: "Files",
      dataIndex: "fileCount",
      render(value) {
        return <SC.ValueSpan>{value || 0}</SC.ValueSpan>;
      },
    },
    {
      title: "No. of Downloads",
      dataIndex: "accessHistoryCount",
      render(value) {
        return <SC.ValueSpan>{value || 0}</SC.ValueSpan>;
      },
    },
    {
      title: "Sent On",
      dataIndex: "createdOn",
      sorter: true,
      render(value) {
        return (
          <SC.ValueSpan>
            <TimezoneConverter time={value} />
          </SC.ValueSpan>
        );
      },
    },
    {
      title: "Action",
      dataIndex: "action",
      width: 110,
      render(value, record) {
        return (
          <Button
            caption="View"
            color="white"
            fontSize="12px"
            onClick={(event) => {
              event?.stopPropagation?.();
              getCartHistoryDetails(record);
            }}
            style={{
              background: theme.gradient.themeGradient,
              color: theme.colors.white,
              minWidth: "70px",
              height: "28px",
              borderRadius: "5px",
            }}
          />
        );
      },
    },
  ];

  return (
    <SC.Container>
      <InnoTable
        tableConfig={historyTableConfig}
        loading={historyLoading}
        padding="5px 8px"
        column={historyColumns}
        data={historyData}
        tableKey=""
        onFilterChange={onFilterChange}
      />
      {selectedHistory && (
        <DetailDrawer
          open={Boolean(selectedHistory)}
          onClose={closeHistoryDrawer}
          width="50%"
          closable={true}
          children={
            <CartHistoryDetails
              historyData={selectedHistory}
              sendTypeLabel={sendTypeLabel}
              onClose={closeHistoryDrawer}
              loading={historyDetailsLoading}
            />
          }
        />
      )}
    </SC.Container>
  );
};

export default CartHistory;
